import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageIcon } from "@/components/PageIcon";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, X, Loader2, Pencil, Trash2, Link2, Search, Briefcase, User, FileText, Receipt, CalendarRange,
} from "lucide-react";

const PRIO = ["Low", "Medium", "High"];
const PRIO_COLOUR = { Low: "bg-slate-100 text-slate-600", Medium: "bg-blue-100 text-blue-700", High: "bg-rose-100 text-rose-700" };
const LINK_ICON = { job: Briefcase, customer: User, quote: FileText, invoice: Receipt, shift: CalendarRange };

export default function MyTasks() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, done: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/me/tasks").then((r) => { setItems(r.data.items || []); setCounts({ open: r.data.open, done: r.data.done }); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    setItems((s) => s.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)));
    try { await api.patch(`/me/tasks/${t.id}`, { done: !t.done }); load(); }
    catch { toast.error("Could not update"); load(); }
  };
  const remove = async (id) => {
    try { await api.delete(`/me/tasks/${id}`); toast.success("Task deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-my-tasks">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <PageIcon />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Tasks</h1>
          <p className="text-sm text-slate-500">Private to you · {counts.open} open · {counts.done} done</p>
        </div>
        <div className="ml-auto">
          <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setEditing({})} data-testid="tasks-add-btn"><Plus className="w-4 h-4" /> Add task</Button>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-slate-400 text-sm mb-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center text-slate-400" data-testid="tasks-empty">
          No tasks yet. Click <span className="font-semibold text-slate-600">Add task</span> to create your first one.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm divide-y divide-slate-100 overflow-hidden" data-testid="tasks-list">
        {items.map((t) => {
          const LI = t.link ? (LINK_ICON[t.link.type] || Link2) : null;
          return (
            <div key={t.id} className="group flex items-center gap-3 px-4 py-3" data-testid={`task-row-${t.id}`}>
              <input type="checkbox" checked={!!t.done} onChange={() => toggle(t)} className="w-4 h-4 accent-indigo-600 shrink-0" data-testid={`task-check-${t.id}`} />
              <div className="min-w-0 flex-1">
                <div className={`font-medium truncate ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${PRIO_COLOUR[t.priority] || PRIO_COLOUR.Medium}`}>{t.priority}</span>
                  {t.due_date && <span className="text-[11px] text-slate-500">Due {t.due_date}</span>}
                  {t.link && (
                    <button onClick={() => t.link.url && navigate(t.link.url)} className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:underline" data-testid={`task-link-${t.id}`}>
                      {LI && <LI className="w-3 h-3" />} {t.link.label}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setEditing(t)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`task-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(t.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`task-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && <TaskEditor task={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TaskEditor({ task, onClose, onSaved }) {
  const editing = !!task.id;
  const [f, setF] = useState({ title: task.title || "", due_date: task.due_date || "", priority: task.priority || "Medium" });
  const [link, setLink] = useState(task.link || null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/me/tasks/${task.id}`, { ...f, link, link_set: true });
      else await api.post("/me/tasks", { ...f, link });
      toast.success(editing ? "Task updated" : "Task added"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const LI = link ? (LINK_ICON[link.type] || Link2) : null;
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose} data-testid="task-editor">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{editing ? "Edit task" : "Add task"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="task-editor-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className="text-xs font-semibold text-slate-500">Title</label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} autoFocus data-testid="task-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-500">Due date</label>
              <Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="task-due-input" /></div>
            <div><label className="text-xs font-semibold text-slate-500">Priority</label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Linked record (optional)</label>
            {link ? (
              <div className="flex items-center gap-2 mt-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm" data-testid="task-linked">
                {LI && <LI className="w-4 h-4 text-indigo-600 shrink-0" />}
                <span className="min-w-0 flex-1 truncate text-indigo-800">{link.label}</span>
                <span className="text-[10px] uppercase text-indigo-400 capitalize">{link.type}</span>
                <button onClick={() => setLink(null)} className="text-indigo-400 hover:text-indigo-700" data-testid="task-link-clear"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <Button variant="outline" className="mt-1 w-full gap-1.5" onClick={() => setPicking(true)} data-testid="task-link-btn"><Link2 className="w-4 h-4" /> Link to a job, shift, quote, invoice or customer</Button>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="task-save-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
        </div>
      </div>
      {picking && <LinkPicker onClose={() => setPicking(false)} onPick={(l) => { setLink(l); setPicking(false); }} />}
    </div>
  );
}

function LinkPicker({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const run = useCallback((term) => {
    setSearching(true);
    api.get("/me/link-search", { params: { q: term } })
      .then((r) => setResults(r.data.items || [])).catch(() => {}).finally(() => setSearching(false));
  }, []);
  useEffect(() => { run(""); }, [run]);

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="link-picker">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-900">Link a record</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="link-picker-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input autoFocus value={q} onChange={(e) => { setQ(e.target.value); run(e.target.value); }} placeholder="Search jobs, customers, quotes, invoices, shifts…" className="pl-9" data-testid="link-picker-search" />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-slate-100" data-testid="link-picker-results">
          {searching && <div className="p-4 flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>}
          {!searching && results.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No matches.</div>}
          {results.map((r, i) => {
            const LI = LINK_ICON[r.type] || Link2;
            return (
              <button key={`${r.type}-${r.ref_id}-${i}`} onClick={() => onPick(r)} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3" data-testid={`link-option-${r.type}-${r.ref_id}`}>
                <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><LI className="w-4 h-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 truncate">{r.label}</div>
                  <div className="text-xs text-slate-500 truncate">{r.sublabel}</div>
                </div>
                <span className="text-[10px] uppercase text-slate-400 capitalize shrink-0">{r.type}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
